import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Stack, Avatar, IconButton } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageLayout from '../../../../components/layout/PageLayout';
import BasicButton from '../../../../components/button/BasicButton';
import CustomTextField from '../../../../components/common/CustomTextField';
import CustomTextareaAutosize from '../../../../components/common/CustomTextareaAutosize';
import CancelConfirmDialog from '../../../../components/dialog/CancelConfirmDialog';
import ProgressBar from '../../../../components/common/ProgressBar';
import Rating from '../../../../components/common/Rating';
import WebIcon from '@mui/icons-material/Web';
import profileAddIcon from '../../../../assets/profile_add.svg';
import profileEditIcon from '../../../../assets/profile_edit.svg';
import api from '../../../../api/api';
import { albumApi } from '../../../../api/album';
import { formatDateForInput } from '../../../../utils/formatter';
import { useAlbumDetailBySlug } from '../../hooks/useAlbumDetail';
import theme from '../../../../theme';

const schema = yup.object().shape({
  sourceImage: yup.mixed().nullable(),
  sourceName: yup.string().required('출처를 입력하세요.'),
  author: yup.string().nullable(),
  rating: yup
    .number()
    .nullable()
    .test('rating-required', '평점을 입력하세요.', (value) => value != null && value > 0),
  originalReviewUrl: yup
    .string()
    .required('원본 리뷰 URL을 입력하세요.')
    .test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
      if (!value) return true;
      return value.startsWith('https://') || value.startsWith('http://');
    }),
  reviewDate: yup.string().required('날짜를 입력하세요.'),
  content: yup.string().required('내용을 입력하세요.'),
});

const CONTENT_MAX_LENGTH = 10000;

const CriticReviewForm = () => {
  const params = useParams();
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const reviewId = params.reviewId;
  const isEditMode = !!reviewId;
  const navigate = useNavigate();
  const { state } = useLocation();
  const queryClient = useQueryClient();

  const slugResult = useAlbumDetailBySlug(albumSlug);
  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;
  const albumUrl = albumSlug ? `/reviews/${albumSlug}` : `/reviews/${albumSource}/${sourceId}`;

  // CriticReviewSection에서 navigate 시 state.albumId로 올바른 albumId를 전달받음
  // state.albumId가 없으면 sourceId로 fallback (직접 URL 접근 시)
  const albumIdForCriticReviews = state?.albumId || sourceId;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      sourceImage: null,
      sourceName: '',
      author: '',
      rating: 0,
      originalReviewUrl: '',
      reviewDate: new Date().toISOString().split('T')[0],
      content: '',
    },
  });

  const rating = watch('rating');
  const content = watch('content');

  const [sourceImagePreview, setSourceImagePreview] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isEditMode && albumIdForCriticReviews) {
      const fetchReview = async () => {
        try {
          const response = await albumApi.getCriticReviews(albumIdForCriticReviews);
          const reviews = response.data;
          const review = reviews.find((r) => r.id === Number(reviewId));
          if (!review) return;

          setValue('sourceName', review.sourceName);
          setValue('author', review.author || '');
          setValue('rating', review.rating || 0);
          setValue('originalReviewUrl', review.originalReviewUrl);
          setValue('reviewDate', formatDateForInput(review.reviewDate));
          setValue('content', review.content);

          if (review.sourceImage) {
            setSourceImagePreview(review.sourceImage);
          }
        } catch (error) {
          console.error('Failed to fetch critic review:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchReview();
    }
  }, [isEditMode, albumIdForCriticReviews, reviewId, setValue]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setValue('sourceImage', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSourceImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (formData) => {
    try {
      let imageUrl = '';
      if (formData.sourceImage) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.sourceImage);
        const { data: response } = await api.postAuth('/images/upload', imageFormData);
        imageUrl = response.data.url;
      }

      const payload = {
        sourceName: formData.sourceName,
        author: formData.author,
        originalReviewUrl: formData.originalReviewUrl,
        reviewDate: formData.reviewDate,
        content: formData.content,
        rating: formData.rating || null,
        sourceImage: imageUrl || sourceImagePreview || null,
      };

      if (isEditMode) {
        await albumApi.updateCriticReview(reviewId, payload);
      } else {
        await albumApi.createCriticReview(sourceId, albumSource, payload);
      }

      if (albumIdForCriticReviews) {
        queryClient.invalidateQueries({ queryKey: ['criticReviews', albumIdForCriticReviews] });
      }

      navigate(albumUrl);
    } catch (error) {
      console.error('Failed to save critic review:', error);
    }
  };

  const handleCancel = () => {
    navigate(albumUrl);
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box sx={{ maxWidth: '780px', mx: 'auto' }}>
          <Stack spacing={{ xs: '30px', md: '30px', lg: '50px' }}>
            <Stack spacing={2.5}>
              {/* 출처 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  출처
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar
                      src={sourceImagePreview || undefined}
                      sx={{ width: 48, height: 48, bgcolor: theme.palette.grey.line }}
                    >
                      {!sourceImagePreview && (
                        <WebIcon sx={{ color: theme.palette.black[2], fontSize: 24 }} />
                      )}
                    </Avatar>
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        padding: 0,
                        width: 14,
                        height: 14,
                      }}
                    >
                      <Box
                        component="img"
                        src={sourceImagePreview ? profileEditIcon : profileAddIcon}
                        alt={sourceImagePreview ? '출처 이미지 수정' : '출처 이미지 추가'}
                        sx={{ width: '100%', height: '100%' }}
                      />
                    </IconButton>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Box>
                  <CustomTextField
                    fullWidth
                    {...register('sourceName')}
                    error={!!errors.sourceName}
                    helperText={errors.sourceName?.message}
                    placeholder="출처를 입력하세요."
                    sx={{ minWidth: 264 }}
                  />
                </Box>
              </Stack>

              {/* 작성자 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  작성자
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('author')}
                  error={!!errors.author}
                  helperText={errors.author?.message}
                  placeholder="작성자를 입력하세요."
                />
              </Stack>

              {/* 평점 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  평점
                </Typography>
                <Rating value={rating} onChange={(val) => setValue('rating', val)} />
                {errors.rating && (
                  <Typography fontSize={12} color="error">
                    {errors.rating.message}
                  </Typography>
                )}
              </Stack>

              {/* 원본 URL */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  원본 리뷰 URL
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('originalReviewUrl')}
                  error={!!errors.originalReviewUrl}
                  helperText={errors.originalReviewUrl?.message}
                  placeholder="원본 리뷰 URL을 입력하세요."
                />
              </Stack>

              {/* 날짜 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  날짜
                </Typography>
                <CustomTextField
                  fullWidth
                  type="date"
                  {...register('reviewDate')}
                  error={!!errors.reviewDate}
                  helperText={errors.reviewDate?.message}
                />
              </Stack>

              {/* 내용 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  내용
                </Typography>
                <CustomTextareaAutosize
                  minRows={{ xs: 4.5, md: 2.5 }}
                  placeholder="앨범에 대한 리뷰를 작성해 주세요."
                  value={content}
                  onChange={(e) => setValue('content', e.target.value)}
                  maxLength={CONTENT_MAX_LENGTH}
                  error={!!errors.content}
                />
                {errors.content && (
                  <Typography fontSize={12} color="error">
                    {errors.content.message}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* 버튼 */}
            <Box width="100%" display="flex" justifyContent="center">
              <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={() => setIsCancelDialogOpen(true)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </BasicButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </ProgressBar>
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancel}
      />
    </PageLayout>
  );
};

export default CriticReviewForm;
